import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GenericSearchControls from '../components/common/GenericSearchControls';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import zoeOrdersService from '../services/zoe-orders-service';

export default function ZoeOrdersPage() {
  const navigate = useNavigate();
  const [zoeOrders, setZoeOrders] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });
  const [error, setError] = useState(null);
  const paginationLimitRef = useRef(20);

  useEffect(() => {
    paginationLimitRef.current = pagination.limit;
  }, [pagination.limit]);

  // restore saved search state
  useEffect(() => {
    const savedSearch = localStorage.getItem('zoeOrdersSearchValue');
    const savedFilters = localStorage.getItem('zoeOrdersAdvancedSearchFilters');
    if (savedSearch) setSearchValue(savedSearch);
    if (savedFilters) setFilters(JSON.parse(savedFilters));
  }, []);

  const fetchAllZoeOrders = useCallback(
    async (page = 1, limit = paginationLimitRef.current) => {
      setLoading(true);
      setError(null);
      try {
        const response = await zoeOrdersService.getList(page, limit);
        console.log('Zoe Orders API Response:', response); // Debug log

        // Handle both success formats: success: true OR code: 0
        if (response.success === true || response.code === 0) {
          setZoeOrders(response.data || []);
          setPagination({
            ...(response.pagination || response.pageInfo),
            limit,
            total: response.total || response.totalCount || 0
          });
        } else {
          setZoeOrders([]);
          setError(response.message || 'Failed to load Zoe orders.');
        }
      } catch (err) {
        console.error('Failed to fetch Zoe orders:', err);
        setError('Failed to load Zoe orders. Please try again.');
      }
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    fetchAllZoeOrders();
  }, [fetchAllZoeOrders]);

  useEffect(() => {
    localStorage.setItem('zoeOrdersSearchValue', searchValue);
  }, [searchValue]);

  useEffect(() => {
    localStorage.setItem(
      'zoeOrdersAdvancedSearchFilters',
      JSON.stringify(filters)
    );
  }, [filters]);

  const handleSearch = useCallback(
    async (page = 1, limit = paginationLimitRef.current) => {
      setLoading(true);
      setError(null);
      try {
        const filterObj = {};
        filters.forEach((f) => {
          if (f.field && f.value) filterObj[f.field] = f.value;
        });
        if (dateFilter !== 'default') {
          filterObj.date_filter = dateFilter;
        }
        filterObj.query = searchValue;

        const response = await zoeOrdersService.searchByListField(
          filterObj,
          page,
          sortBy !== 'default' ? sortBy : undefined,
          sortOrder !== 'default' ? sortOrder : undefined,
          limit
        );

        console.log('Zoe Orders Search Response:', response); // Debug log

        // Handle both success formats: success: true OR code: 0
        if (response.success === true || response.code === 0) {
          setZoeOrders(response.data || []);
          setPagination({
            ...(response.pagination || response.pageInfo),
            limit,
            total: response.total || response.totalCount || 0
          });
        } else {
          setZoeOrders([]);
          setError(response.message || 'Search failed. Please try again.');
        }
      } catch (err) {
        console.error('Failed to search Zoe orders:', err);
        setError('Search failed. Please try again.');
      }
      setLoading(false);
    },
    [searchValue, filters, dateFilter, sortBy, sortOrder]
  );

  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  const debouncedSearch = useRef(
    debounce((page, limit) => handleSearchRef.current(page, limit), 500)
  ).current;

  // Keyed on [] deliberately: debouncedSearch is built with
  // useRef(debounce(...)).current, so its identity never changes and a
  // [debouncedSearch]-keyed cleanup would never fire.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Shared with PaginatedControls so paging uses the same search-vs-fetchAll
  // decision as this effect.
  const hasSearchCriteria =
    !!searchValue ||
    filters.length > 0 ||
    dateFilter !== 'default' ||
    sortBy !== 'default' ||
    sortOrder !== 'default';

  useEffect(() => {
    // Reset to page 1 unconditionally, then either search or fall back to the
    // unfiltered list. The `else` is the fix: with no branch here, clearing
    // the last filter left the previous filtered results frozen on screen
    // while the search UI showed empty — it looked like clearing did nothing.
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (hasSearchCriteria) {
      debouncedSearch(1, pagination.limit);
    } else {
      // Cancel any in-flight debounced search so a slower stale response
      // cannot land afterwards and re-apply the filter just cleared.
      debouncedSearch.cancel();
      fetchAllZoeOrders(1, pagination.limit);
    }
  }, [
    searchValue,
    filters,
    dateFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    debouncedSearch,
    hasSearchCriteria,
    fetchAllZoeOrders
  ]);

  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'order_number':
        return 'Order Number';
      case 'customer_name':
        return 'Customer Name';
      case 'customer_email':
        return 'Customer Email';
      case 'order_status':
        return 'Order Status';
      case 'vendor_code':
        return 'Vendor Code';
      case 'vendor_name':
        return 'Vendor Name';
      case 'order_type':
        return 'Order Type';
      case 'rms_po_number':
        return 'RMS PO Number';
      case 'reference_id':
        return 'Reference ID';
      case 'status':
        return 'Status';
      default:
        return field;
    }
  };

  const getOrderStatusColor = (status) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'shipped':
        return 'primary';
      case 'partially_shipped':
        return 'secondary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'returned':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const lower = status.toLowerCase();

    if (lower.includes('created') || lower.includes('rmspocreated'))
      return 'info';
    if (lower.includes('shipped')) return 'primary';
    if (lower.includes('delivered')) return 'success';
    if (lower.includes('cancelled')) return 'error';
    if (lower.includes('pending')) return 'warning';

    return 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const filterOptions = [
    { value: 'order_number', label: 'Order Number' },
    { value: 'customer_name', label: 'Customer Name' },
    { value: 'customer_email', label: 'Customer Email' },
    { value: 'order_status', label: 'Order Status' },
    { value: 'vendor_code', label: 'Vendor Code' },
    { value: 'vendor_name', label: 'Vendor Name' },
    { value: 'order_type', label: 'Order Type' },
    { value: 'rms_po_number', label: 'RMS PO Number' },
    { value: 'reference_id', label: 'Reference ID' },
    { value: 'status', label: 'Status' }
  ];

  const sortByList = [
    { value: 'ZoeOrderId', label: 'Order ID' },
    { value: 'OrderNumber', label: 'Order Number' },
    { value: 'CustomerName', label: 'Customer Name' },
    { value: 'OrderStatus', label: 'Order Status' },
    { value: 'OrderDate', label: 'Order Date' },
    { value: 'CreatedDate', label: 'Date Created' },
    { value: 'Total', label: 'Total Amount' },
    { value: 'VendorName', label: 'Vendor' },
    { value: 'OrderType', label: 'Order Type' }
  ];

  return (
    <div>
      <div style={{ marginTop: 40, padding: 20 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Zoe Orders
        </Typography>
        <div style={{ margin: '20px auto', width: '80%' }}>
          <GenericSearchControls
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            searchFieldLabel="Order Number / Customer Name"
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            enableDateFilter={true}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            handleSearchClick={handleSearchClick}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
            resetAll={() => {
              setSearchValue('');
              setFilters([]);
              setDateFilter('default');
              setSortBy('default');
              setSortOrder('default');
              setPagination({ currentPage: 1, totalPages: 1, limit: 20 });
              setShowAdvancedSearch(false);
              setFilterDraft({ field: '', value: '' });
              localStorage.removeItem('searchValue');
              localStorage.removeItem('advancedSearchFilters');
              fetchAllZoeOrders();
            }}
          />

          {(searchValue || filters.length > 0 || dateFilter !== 'default') && (
            <SearchQuerySummary
              searchValue={searchValue}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
              searchFieldLabel="Order Number / Customer Name"
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={zoeOrders.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllZoeOrders}
            isSearchActive={hasSearchCriteria}
          />

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : zoeOrders.length === 0 ? (
            <Paper sx={{ padding: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Records Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchValue || filters.length > 0
                  ? 'No Zoe orders match your search criteria. Try adjusting your filters.'
                  : 'No Zoe orders available.'}
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Order Number</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Order Status</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Order Type</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {zoeOrders.map((order) => (
                    <TableRow key={order.Id}>
                      <TableCell>{order.Id}</TableCell>
                      <TableCell>{order.OrderNumber || 'N/A'}</TableCell>
                      <TableCell>
                        {order.CustomerName || order.CustomerEmail || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.OrderStatus || 'Unknown'}
                          color={getOrderStatusColor(order.OrderStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {order.Status ? (
                          <Chip
                            label={order.Status}
                            color={getStatusColor(order.Status)}
                            variant="outlined"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{order.OrderType || 'N/A'}</TableCell>
                      <TableCell>
                        {order.VendorName || order.VendorCode || 'N/A'}
                      </TableCell>
                      <TableCell>{formatCurrency(order.Total)}</TableCell>
                      <TableCell>{formatDate(order.OrderDate)}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => navigate(`/zoe-orders/${order.Id}`)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={zoeOrders.length}
            searchValue={searchValue}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllZoeOrders}
            isSearchActive={hasSearchCriteria}
          />
        </div>
      </div>
    </div>
  );
}
